import { Box, Divider, Stack, Typography, IconButton } from "@mui/material";
import {
	DeviceState,
	DeviceStatus,
} from "Infrastructure/Models/Messages/Dto/DeviceStatus";
import { DeviceForm } from "Infrastructure/Utils/Nsure/DeviceUtils";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { HubConnection } from "@microsoft/signalr";
import { CloseDialogHandler } from "Infrastructure/Providers/DialogProvider/DialogProvider";
import DeviceFormComponent from "../DeviceFormComponent";
import {
	FeatureEnableDisableMessage,
	FeatureUpdateMessage,
} from "Infrastructure/Models/Messages/Messages";
import FeatureActionUpdate from "./FeatureActionUpdate";
import { FeatureDeviceModel } from "Infrastructure/Utils/Nsure/FeatureUtils";
import FeatureListTable from "./FeatureListTable";
import { ActionHistorySelect } from "../ActionHistorySelect";
import { ActionHistoryDto } from "Infrastructure/Api/Api";
import { ActionType } from "Infrastructure/Api/ApiCustom";
import ClearIcon from "@mui/icons-material/Clear";
import { FeatureEnableDisableModel } from "Infrastructure/Models/Messages/Messages";
import { useSnackbar } from "Infrastructure/Providers/SnackbarProvider/SnackbarProvider";
import {
	FeatureUpdateFormData,
	getNewFormData as getNewFeatureUpdateFormData,
} from "./FeatureActionUpdate";

type Props = {
	deviceStatuses: DeviceStatus[] | null;
	deviceStatus?: DeviceStatus | undefined;
	featureDeviceList: FeatureDeviceModel[];
	hubConnection: HubConnection | null;
	closeDialog: CloseDialogHandler;
	onUpdate: (message: FeatureUpdateMessage) => void;
	onEnableDisable: (message: FeatureEnableDisableMessage) => void;
};

const FeatureActions: FunctionComponent<Props> = (props) => {
	const {
		deviceStatuses,
		deviceStatus,
		featureDeviceList,
		hubConnection,
		closeDialog,
		onUpdate,
		onEnableDisable,
	} = props;
	const showSnackbar = useSnackbar();

	if (!deviceStatuses || deviceStatuses?.length === 0) {
		return (
			<Typography variant="caption">Žádné instance k dispozici</Typography>
		);
	}

	const [form, setForm] = useState<DeviceForm>({
		devices: !!deviceStatus
			? [
					{
						name: deviceStatus.deviceName,
						isChecked: true,
						isDisabled: false,
						deviceStatus,
					},
			  ]
			: deviceStatuses.map((x) => ({
					name: x.deviceName,
					isChecked: false,
					isDisabled:
						x.deviceState !== DeviceState.Connected || x.isSelfInsurance,
					deviceStatus: x,
			  })),
	});

	// Wrap setForm to clear isHistoryLoaded on any change
	const setFormWithHistoryCheck = (
		updater: React.SetStateAction<DeviceForm>
	) => {
		setForm((prev: DeviceForm) => {
			const next = typeof updater === "function" ? updater(prev) : updater;
			if (isHistoryLoaded && !justLoadedHistory.current) {
				setIsHistoryLoaded(false);
				setSelectedHistory(null);
			}
			return next;
		});
	};

	// Accordion logic
	const [expandedAccordion, setExpandedAccordion] = useState<string | false>(
		false
	);
	const handleAccordionChange =
		(accordion: string) => (_event: any, isExpanded: boolean) => {
			setExpandedAccordion(isExpanded ? accordion : false);
		};

	const [featuresToConfirm, setFeaturesToConfirm] = useState<
		FeatureEnableDisableModel[]
	>([]);
	const [formData, setFormData] = useState<FeatureUpdateFormData>({
		isValidationShown: false,
		code: { value: "", isValid: true },
		isEnabled: { value: false, isValid: true },
		isSelfInsEnabled: { value: false, isValid: true },
		reason: { value: "", isValid: true },
	});

	const [selectedHistory, setSelectedHistory] =
		useState<ActionHistoryDto | null>(null);
	const [isHistoryLoaded, setIsHistoryLoaded] = useState(false);
	const justLoadedHistory = useRef(false);

	// Add mapping functions
	function mapFeatureEnableDisableToForm(
		features: any[]
	): FeatureEnableDisableModel[] {
		return features.map((f) => ({
			featureCode: f.FeatureCode ?? f.featureCode ?? null,
			doEnable: f.DoEnable ?? f.doEnable ?? null,
		}));
	}
	function mapFeatureUpdateToFormData(message: any): FeatureUpdateFormData {
		const feature = message.Feature ?? message.feature ?? {};
		return {
			isValidationShown: false,
			code: { value: feature.Code ?? feature.code ?? "", isValid: true },
			isEnabled: {
				value: feature.IsEnabled ?? feature.isEnabled ?? false,
				isValid: true,
			},
			isSelfInsEnabled: {
				value: feature.IsSelfInsEnabled ?? feature.isSelfInsEnabled ?? false,
				isValid: true,
			},
			// reason: { value: message.Reason ?? message.reason ?? "", isValid: true },
			reason: { value: "", isValid: true },
		};
	}

	// Utility: Map filteredDeviceNames to deviceForm
	function updateDeviceFormWithFilteredNames(
		deviceForm: DeviceForm,
		filteredDeviceNames: string[] = []
	) {
		const unavailableDevices: string[] = [];
		
		const updatedDevices = deviceForm.devices.map((device) => {
			const shouldBeChecked = filteredDeviceNames.includes(device.name);
			
			// If device should be checked but is disabled, add to unavailable list
			if (shouldBeChecked && device.isDisabled) {
				unavailableDevices.push(device.name);
			}
			
			return {
				...device,
				isChecked: shouldBeChecked && !device.isDisabled, // Only check if not disabled
			};
		});
		
		// Show snackbar if there are unavailable devices
		if (unavailableDevices.length > 0) {
			showSnackbar(
				`Nepodařilo se načíst některé instance: ${unavailableDevices.join(', ')}`,
				"error"
			);
		}
		
		return {
			...deviceForm,
			devices: updatedDevices,
		};
	}

	// Effect: clear history selection on form change
	// biome-ignore lint/correctness/useExhaustiveDependencies: form, featuresToConfirm, formData are intentional change triggers
	useEffect(() => {
		if (isHistoryLoaded) {
			if (justLoadedHistory.current) {
				justLoadedHistory.current = false;
			} else {
				// Clear history when any form data changes manually
				setIsHistoryLoaded(false);
				setSelectedHistory(null);
			}
		}
	}, [form, featuresToConfirm, formData, isHistoryLoaded]);

	const clearAllData = () => {
		setForm((prev) => ({
			...prev,
			devices: prev.devices.map((d) => ({ ...d, isChecked: false })),
		}));
		setFeaturesToConfirm([]);
		setFormData(getNewFeatureUpdateFormData());
		setSelectedHistory(null);
		setIsHistoryLoaded(false);
	};

	return (
		<Stack>
			<Box>
				<Stack
					direction="row"
					spacing={1}
					alignItems="center"
					width="900px"
					justifySelf="end"
				>
					<ActionHistorySelect
						device={deviceStatus?.deviceName}
						actionTypes={[
							ActionType.FeatureEnableDisable,
							ActionType.FeatureUpdate,
						]}
						value={selectedHistory}
						onSelect={(action) => {
							clearAllData();
							setSelectedHistory(action);
							if (action) {
								try {
									const parsed = JSON.parse(action.messageJson);
									const filteredDeviceNames = parsed.FilteredDeviceNames || parsed.filteredDeviceNames || [];
									setFormWithHistoryCheck((prev) => updateDeviceFormWithFilteredNames(prev, filteredDeviceNames));
									if (action.actionType === ActionType.FeatureEnableDisable) {
										setFeaturesToConfirm(
											mapFeatureEnableDisableToForm(
												parsed.Features ?? parsed.features ?? []
											)
										);
									} else if (action.actionType === ActionType.FeatureUpdate) {
										setFormData(
											mapFeatureUpdateToFormData(parsed)
										);
									}
									setIsHistoryLoaded(true);
									justLoadedHistory.current = true;
								} catch {
									// showSnackbar("Nepodařilo se načíst historickou akci.", "error");
								}
							}
						}}
						disabled={isHistoryLoaded}
					/>
					<IconButton
						size="small"
						onClick={clearAllData}
						disabled={!isHistoryLoaded}
					>
						<ClearIcon sx={{ fontSize: "1.2rem" }} />
					</IconButton>
				</Stack>
			</Box>
			{!deviceStatus && (
				<Box mb={1}>
					<Typography variant="h6" component="div">
						Instance
					</Typography>
					<Divider />
					<Box ml={1.5} mt={1}>
						<DeviceFormComponent
							deviceStatuses={deviceStatuses}
							hubConnection={hubConnection}
							deviceStatus={deviceStatus}
							form={form}
							setForm={setFormWithHistoryCheck}
						/>
					</Box>
				</Box>
			)}
			<Stack>
				<Box>
					<Typography variant="h6" component="div">
						Akce
					</Typography>
					<Divider />
				</Box>
				<FeatureActionUpdate
					lacksSelfInsInstance={!deviceStatus?.hasSelfInsInstance}
					deviceForm={form}
					featureDeviceList={featureDeviceList}
					hubConnection={hubConnection}
					closeDialog={closeDialog}
					onUpdate={onUpdate}
					formData={formData}
					setFormData={setFormData}
				/>
				<Box mt={2}>
					<Box my={1}>
						<Typography variant="h6" component="div">
							Data
						</Typography>
						<Divider />
					</Box>
					<FeatureListTable
						deviceForm={form}
						featureDeviceList={featureDeviceList}
						hubConnection={hubConnection}
						closeDialog={closeDialog}
						onEnableDisable={onEnableDisable}
						featuresToConfirm={featuresToConfirm}
						setFeaturesToConfirm={setFeaturesToConfirm}
						isHistoryLoaded={isHistoryLoaded}
					/>
				</Box>
			</Stack>
		</Stack>
	);
};

export default FeatureActions;
